import * as _ from 'lodash-es'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { Select } from '@/components/forms/Select'
import { getDealers } from '@/services/dealers.service'
import { DashboardStoreContext } from '@/stores/DashboardStore'
import { UserStoreContext } from '@/stores/UserStore'

import { ContactDetailsSection } from './ContactDetailsSection'

export const DealerHeader = ({ dealer, isWpcExecutive, isROServiceUser }) => {
  const [dealerOptions, setDealerOptions] = useState([])
  const [, setLoadingDealers] = useState(true)

  const navigate = useNavigate()
  const location = useLocation()
  const { type } = useContext(DashboardStoreContext)
  const { isWpcUser } = useContext(UserStoreContext)

  // Determine current tab from the URL path
  const currentPath = location.pathname.split('/').pop()

  const tabs = useMemo(() => {
    const t = [{ label: 'Outstanding List', path: 'detail' }]
    if (dealer?.isRoIntakeEnabled) {
      t.push({ label: 'RO Intake', path: 'intake' })
    }
    if (isWpcUser) {
      t.push({ label: 'Billing', path: 'billing' })
    }
    return t
  }, [isWpcUser, dealer?.isRoIntakeEnabled])

  useEffect(() => {
    if (!dealer) return

    const typeOfDealers = isWpcExecutive
      ? `?${type === 'wpc' ? 'is_dealer_managed=false' : 'is_dealer_managed=true'}`
      : ''

    const fetchDealers = async () => {
      const dealers = await getDealers(`/dealers/all${typeOfDealers}`, setLoadingDealers)
      if (dealers) setDealerOptions(_.map(dealers, (d) => ({ id: d.id, label: d.name })))
    }

    fetchDealers()
  }, [dealer?.id, type, isWpcExecutive])

  return (
    <div className="mb-4 flex min-h-[4rem] w-full flex-col">
      <div className="flex h-auto w-full flex-col justify-between md:flex-row">
        <div>
          {dealer?.name ? (
            <Select
              className="border-none bg-transparent pl-0 shadow-none"
              options={dealerOptions}
              optionsClassName="min-w-[400px]"
              disabled={dealerOptions.length === 0}
              value={
                _.find(dealerOptions, { id: dealer.id }) ||
                (dealer.id ? { id: dealer.id, label: dealer.name } : null)
              }
              onChange={(selectedDealer) => {
                navigate(`/dealer/${selectedDealer.id}/${currentPath}`, {
                  state: { dealer: selectedDealer },
                })
              }}
              placeholder={dealer?.name}
            />
          ) : (
            <div className="bg-charcoal-200 mt-3 h-6 w-64 animate-pulse rounded-md pl-0"></div>
          )}

          {dealer?.id && tabs.length > 1 && (
            <div className="bg-charcoal-100 mt-2 inline-flex rounded-lg p-1">
              {tabs.map((tab) => (
                <Link
                  key={tab.path}
                  to={`/dealer/${dealer.id}/${tab.path}`}
                  className={mergeClassNames(
                    'rounded-md px-4 py-1.5 text-xs font-bold uppercase tracking-widest transition-all',
                    currentPath === tab.path
                      ? 'bg-white text-blue-800 shadow-sm'
                      : 'text-charcoal-400 hover:text-charcoal-900',
                  )}
                >
                  {tab.label}
                </Link>
              ))}
            </div>
          )}
        </div>

        <ContactDetailsSection
          dealer={dealer}
          isROServiceUser={isROServiceUser}
          loading={!dealer}
        />
      </div>
    </div>
  )
}

DealerHeader.propTypes = {
  dealer: PropTypes.object,
  isWpcExecutive: PropTypes.bool.isRequired,
  isROServiceUser: PropTypes.bool.isRequired,
}
